import {
  AUTH_SESSION_KEYS,
  SESSION_TYPES,
  SESSION_INDEX_KEY,
  STORAGE_KEYS,
  MIGRATION_KEYS,
} from './sessionConstants';

export const buildPrefix = (type, userId, resourceId = null) => {
  if (!userId) return '';
  try {
    const byType = {
      [SESSION_TYPES.AUTOMY]: `automy_${userId}_`,
      [SESSION_TYPES.PORTAL]: `portal_${userId}_${resourceId}_`,
      [SESSION_TYPES.APP]: `app_${userId}_${resourceId}_`,
    };
    return byType[type] ?? '';
  } catch (e) {
    return '';
  }
};

/**
 * Parses a sessionId string back into its session context components.
 * Inverse of buildSessionId — used when only the sessionId is known (e.g. inside
 * the token refresh callback) and the caller needs to target the correct storage namespace.
 */
export const sessionIdToContext = (sessionId) => {
  if (!sessionId) return null;
  try {
    if (sessionId.startsWith('automy_')) {
      const userId = sessionId.slice('automy_'.length);
      return userId ? { type: SESSION_TYPES.AUTOMY, userId, resourceId: null } : null;
    }

    if (sessionId.startsWith('portal_')) {
      const middle = sessionId.slice('portal_'.length);
      const idx = middle.indexOf('_');
      if (idx === -1) return null;
      return {
        type: SESSION_TYPES.PORTAL,
        userId: middle.slice(0, idx),
        resourceId: middle.slice(idx + 1),
      };
    }

    if (sessionId.startsWith('app_')) {
      const middle = sessionId.slice('app_'.length);
      const idx = middle.indexOf('_');
      if (idx === -1) return null;
      return {
        type: SESSION_TYPES.APP,
        userId: middle.slice(0, idx),
        resourceId: middle.slice(idx + 1),
      };
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const buildSessionId = (type, userId, resourceId = null) => {
  if (!userId) return null;
  try {
    const byType = {
      [SESSION_TYPES.AUTOMY]: `automy_${userId}`,
      [SESSION_TYPES.PORTAL]: `portal_${userId}_${resourceId}`,
      [SESSION_TYPES.APP]: `app_${userId}_${resourceId}`,
    };
    return byType[type] ?? null;
  } catch (e) {
    return null;
  }
};

const getPathname = () => {
  try {
    return window.location.pathname || '';
  } catch (e) {
    return '';
  }
};

/**
 * App id from the runtime viewer `/apps/:id` only.
 * Admin routes like `/apps/:id/builder` stay on the AUTOMY token — they are
 * not the public/self-managed app shell.
 */
export const getAppIdFromPath = () => {
  const match = getPathname().match(/^\/apps\/([^/]+)\/?$/);
  return match ? match[1] : null;
};

export const isAppPath = () => !!getAppIdFromPath();

/** Self-managed / public apps store the token as `app_{appId}_{appId}_`. */
export const isSelfManagedAppSession = (session) =>
  session?.type === SESSION_TYPES.APP &&
  !!session.userId &&
  String(session.userId) === String(session.resourceId);

const parseStoredToken = (raw) => {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return data?.accessToken ? data : null;
  } catch (e) {
    return null;
  }
};

/**
 * Attempt to restore a session index from localStorage when sessionStorage is empty.
 * This happens when the user opens a link in a new tab (Ctrl+Click) because
 * sessionStorage is per-tab, but the actual token data lives in localStorage.
 */
const tryRestoreSession = () => {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) return null;

    const suffix = `_${STORAGE_KEYS.AUTH_TOKEN}`;
    // Never restore an APP session here. A public/self-managed app token is not
    // the user's AUTOMY identity — picking it on `/` or a new tab would attach
    // that app's Bearer to every Automy request. AppGateway restores APP
    // sessions explicitly on `/apps/:id`.
    const SESSION_PRIORITY = {
      [SESSION_TYPES.AUTOMY]: 2,
      [SESSION_TYPES.PORTAL]: 2,
    };
    let best = null;
    let bestIssuedAt = -1;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.endsWith(suffix)) continue;

      let candidate = null;

      if (key.startsWith('automy_')) {
        const userId = key.slice('automy_'.length, key.length - suffix.length);
        if (userId) {
          candidate = { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
        }
      } else if (key.startsWith('portal_')) {
        const middle = key.slice('portal_'.length, key.length - suffix.length);
        const parts = middle.split('_');
        if (parts.length >= 2) {
          candidate = {
            type: SESSION_TYPES.PORTAL,
            userId: parts[0],
            resourceId: parts.slice(1).join('_'),
          };
        }
      }

      if (!candidate) continue;

      try {
        const tokenData = JSON.parse(localStorage.getItem(key));
        if (!tokenData?.accessToken) continue;
        const issuedAt = tokenData.issuedAt || 0;
        const candidatePriority = SESSION_PRIORITY[candidate.type] || 0;
        const bestPriority = best ? SESSION_PRIORITY[best.type] || 0 : -1;

        if (
          candidatePriority > bestPriority ||
          (candidatePriority === bestPriority && issuedAt > bestIssuedAt)
        ) {
          best = candidate;
          bestIssuedAt = issuedAt;
        }
      } catch (e) {
        continue;
      }
    }

    if (best) {
      setCurrentSession(best);
      return best;
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const getCurrentSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
    const session = raw ? JSON.parse(raw) : tryRestoreSession();
    if (!session) return null;

    // An APP index is only valid on `/apps/:id`. Leaving it active on Automy
    // routes makes getToken()/getSessionItem() attach that app's token (often
    // a public/self-managed one that is not the signed-in user).
    if (session.type === SESSION_TYPES.APP && !getAppIdFromPath()) {
      const automy = resolveAutomySession(session);
      setCurrentSession(automy);
      return automy;
    }

    return session;
  } catch (e) {
    return null;
  }
};

export const setCurrentSession = (session) => {
  try {
    if (session === null || session === undefined) sessionStorage.removeItem(SESSION_INDEX_KEY);
    else sessionStorage.setItem(SESSION_INDEX_KEY, JSON.stringify(session));
  } catch (e) {}
};

export const getCurrentPrefix = () => {
  try {
    const session = getCurrentSession();
    if (!session) return '';
    return buildPrefix(session.type, session.userId, session.resourceId);
  } catch (e) {
    return '';
  }
};

export const getCurrentSessionId = () => {
  try {
    const session = getCurrentSession();
    if (!session) return null;
    return buildSessionId(session.type, session.userId, session.resourceId);
  } catch (e) {
    return null;
  }
};

export const getItem = (type, userId, resourceId, baseKey) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    return localStorage.getItem(prefix + baseKey);
  } catch (e) {
    return null;
  }
};

export const setItem = (type, userId, resourceId, baseKey, value) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    if (!prefix) return;
    else localStorage.setItem(prefix + baseKey, value);
  } catch (e) {}
};

export const removeItem = (type, userId, resourceId, baseKey) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    localStorage.removeItem(prefix + baseKey);
  } catch (e) {}
};

export const getSessionItem = (baseKey) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) return localStorage.getItem(prefix + baseKey);
    }
    return sessionStorage.getItem(baseKey);
  } catch (e) {
    return null;
  }
};

export const setSessionItem = (baseKey, value) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) {
        localStorage.setItem(prefix + baseKey, value);
        return;
      }
    }
    sessionStorage.setItem(baseKey, value);
  } catch (e) {}
};

export const removeSessionItem = (baseKey) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) {
        localStorage.removeItem(prefix + baseKey);
        return;
      }
    }
    sessionStorage.removeItem(baseKey);
  } catch (e) {}
};

export const clearSession = (type, userId, resourceId) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    if (!prefix) return;

    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

/**
 * Drop the credential keys of every namespace (automy/portal/app) belonging to
 * a user, leaving non-auth data such as the portal branding config in place.
 *
 * Removing only the active namespace is not enough: a token surviving in another
 * namespace is resurrected later by tryRestoreSession, which puts the tab on a
 * session the shell cannot read.
 */
export const clearUserAuthTokens = (userId) => {
  if (!userId) return;
  try {
    const prefixes = [`automy_${userId}_`, `portal_${userId}_`, `app_${userId}_`];
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!prefixes.some((p) => key.startsWith(p))) continue;
      if (AUTH_SESSION_KEYS.some((authKey) => key.endsWith(authKey))) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

export const clearAllUserSessions = (userId) => {
  if (!userId) return;
  try {
    const prefixes = [`automy_${userId}_`, `portal_${userId}_`, `app_${userId}_`];
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

const isAppCrossContext = (fromCtx, toCtx) => {
  const fromApp = fromCtx?.type === SESSION_TYPES.APP;
  const toApp = toCtx?.type === SESSION_TYPES.APP;
  return fromApp !== toApp;
};

export const migrateSession = (fromCtx, toCtx, keys = MIGRATION_KEYS) => {
  try {
    if (isAppCrossContext(fromCtx, toCtx)) return;

    const fromPrefix = buildPrefix(fromCtx.type, fromCtx.userId, fromCtx.resourceId);
    const toPrefix = buildPrefix(toCtx.type, toCtx.userId, toCtx.resourceId);

    if (!fromPrefix || !toPrefix) return;

    keys.forEach((key) => {
      try {
        const value = localStorage.getItem(fromPrefix + key);
        if (value !== null) {
          localStorage.setItem(toPrefix + key, value);
          localStorage.removeItem(fromPrefix + key);
        }
      } catch (e) {}
    });
  } catch (e) {}
};

export const copySession = (fromCtx, toCtx, keys = MIGRATION_KEYS) => {
  try {
    if (isAppCrossContext(fromCtx, toCtx)) return;

    const fromPrefix = buildPrefix(fromCtx.type, fromCtx.userId, fromCtx.resourceId);
    const toPrefix = buildPrefix(toCtx.type, toCtx.userId, toCtx.resourceId);

    if (!fromPrefix || !toPrefix) return;

    keys.forEach((key) => {
      try {
        const value = localStorage.getItem(fromPrefix + key);
        if (value !== null) {
          localStorage.setItem(toPrefix + key, value);
          // No removeItem — source keys preserved for the original tab
        }
      } catch (e) {}
    });
  } catch (e) {}
};

/**
 * Move the active session index from an APP context back to AUTOMY, which is
 * what every non `/apps/*` route expects.
 *
 * Tokens are never copied: an APP refresh token is bound to that app (and a
 * public/self-managed token is not the Automy user). Switching the index is
 * enough; each namespace keeps its own credentials.
 *
 * @returns {boolean} true when the session index was changed.
 */
export const revertAppSessionToAutomy = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
    const session = raw ? JSON.parse(raw) : null;
    if (session?.type !== SESSION_TYPES.APP || !session.userId) return false;

    setCurrentSession(resolveAutomySession(session));
    return true;
  } catch (e) {
    return false;
  }
};

export const findAutomyUserId = () => {
  try {
    const prefix = 'automy_';
    const suffix = `_${STORAGE_KEYS.AUTH_TOKEN}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key.endsWith(suffix)) {
        const userId = key.slice(prefix.length, key.length - suffix.length);
        if (userId) return userId;
      }
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * AUTOMY session to use after leaving an APP context.
 * Public/self-managed apps reuse the appId as userId — that must never become
 * an AUTOMY index, or every Automy request would carry the public app token.
 */
export const resolveAutomySession = (appSession) => {
  if (!appSession?.userId) return null;
  if (isSelfManagedAppSession(appSession)) {
    const automyUserId = findAutomyUserId();
    return automyUserId
      ? { type: SESSION_TYPES.AUTOMY, userId: automyUserId, resourceId: null }
      : null;
  }
  return { type: SESSION_TYPES.AUTOMY, userId: appSession.userId, resourceId: null };
};

/**
 * If the token was refreshed while in a PORTAL context, propagate the newer
 * token back to the AUTOMY namespace so it stays current.
 *
 * APP tokens must never be copied: they are bound to an `appId` and refreshing
 * them as AUTOMY fails with "issued to a different app".
 */
export const syncRefreshedToken = (fromType, userId, resourceId) => {
  try {
    if (!fromType || fromType === SESSION_TYPES.APP) return;

    const fromToken = getItem(fromType, userId, resourceId, STORAGE_KEYS.AUTH_TOKEN);
    const automyToken = getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN);
    if (!fromToken) return;

    const fromData = JSON.parse(fromToken);
    const automyData = automyToken ? JSON.parse(automyToken) : null;

    if (
      fromData?.accessToken &&
      (!automyData || (fromData.issuedAt || 0) > (automyData.issuedAt || 0))
    ) {
      setItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN, fromToken);
    }
  } catch (e) {}
};

export const getSessionToken = () => {
  try {
    const appId = getAppIdFromPath();

    // App routes: only that app's token. Never the AUTOMY/PORTAL Bearer.
    if (appId) {
      const session = getCurrentSession();
      if (session?.type === SESSION_TYPES.APP && String(session.resourceId) === String(appId)) {
        const token = parseStoredToken(
          getItem(SESSION_TYPES.APP, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
        );
        if (token) return token;
      }

      const ownerId =
        session?.userId && !isSelfManagedAppSession(session) ? session.userId : findAutomyUserId();
      if (ownerId) {
        const owned = parseStoredToken(
          getItem(SESSION_TYPES.APP, ownerId, String(appId), STORAGE_KEYS.AUTH_TOKEN),
        );
        if (owned) return owned;
      }

      return parseStoredToken(
        getItem(SESSION_TYPES.APP, String(appId), String(appId), STORAGE_KEYS.AUTH_TOKEN),
      );
    }

    // Automy / portal routes: never an APP token.
    const session = getCurrentSession();
    if (!session || session.type === SESSION_TYPES.APP) return null;

    return parseStoredToken(
      getItem(session.type, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
    );
  } catch (e) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Non-session-scoped storage helpers
// ---------------------------------------------------------------------------
// "Global" items live in localStorage but are NOT prefixed by any session.
// Use for pre-authentication flags that must survive full-page redirects
// (e.g. OAuth round-trips) before a session exists.
// ---------------------------------------------------------------------------
export const getGlobalItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const setGlobalItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {}
};

export const removeGlobalItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {}
};

// "Tab" items live in sessionStorage — cleared when the tab closes.
// Use for ephemeral caches that should not leak across tabs.
export const getTabItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const setTabItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
};

export const removeTabItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {}
};
